import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { brandName } from '#app/components/branding.js'
import { getSkillsByServerName } from '#app/utils/skills.server.js'
import { getServerAndShardList, getServerDetailsBySlug } from '#app/utils/shards.server.js'
import { prettyDateShort } from '#app/utils/dateFormat.js'
import { prisma } from '#app/utils/db.server.js'
import { useOptionalUser } from '#app/utils/user.js'
import { getUserImgSrc } from '#app/utils/misc.js'

export async function loader({ params }: LoaderFunctionArgs) {

	const serverDetails = await getServerDetailsBySlug(params.serverName || "")
    invariantResponse(serverDetails, 'Server not found', { status: 404 })

    const shardRecord = serverDetails?.shards.find(s => s.slug === params.shardName || "");
    invariantResponse(shardRecord, 'Shard not found', { status: 404 })

	
	const charactersOnServer = await prisma.character.findMany({
		where: { shardId: shardRecord.id },
		include: {
		  user: {
			select: {
			  id: true,
			  username: true,
			  image: true
			},
		  },
		},
	  });
	  


	return json({ serverDetails, shardDetails: shardRecord, charactersOnServer })
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const serverDetails = data.serverDetails
    const shardDetails = data.shardDetails
	const loggedInUser = useOptionalUser()

	return (
		<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
		<div className="flex flex-wrap items-center justify-center">
			<h1 className="text-center text-h2">{serverDetails.name}: {shardDetails.name} Characters ({data.charactersOnServer.length})</h1>
		</div>

		<div className="mt-2 text-center text-muted-foreground flex flex-row">
			{serverDetails.establishedAt ? <div className="text-muted-foreground mr-4">Established: {prettyDateShort(serverDetails.establishedAt)}</div> : null}
				<div>Website: <a href={serverDetails.website} className="hover:no-underline"><span className="underline">{serverDetails.website}</span>
					<ArrowTopRightOnSquareIcon aria-hidden="true" className="h-6 w-6 inline p-1" />
			</a></div>
		</div>
		<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">

				<div className="mb-8">
					<div className="text-center">
						<Link to={`/servers/${serverDetails.slug}/shards/${shardDetails.slug}`} className="hover:underline">
							<img
								src={serverDetails.logo}
								alt={serverDetails.name}
								className=""
							/>
							Back to {shardDetails.name} shard
                        </Link>
					</div>
				</div>


                <div className='mb-8 text-center text-h3'>
                    Did you ever play on {serverDetails.name}?  Create <Link to={loggedInUser ? `/users/${loggedInUser.username}/characters/new?server=${serverDetails.name}&shard=${shardDetails.name}` : `/create-new-character`} className="underline hover:no-underline">your character</Link> and share your story!
                </div>

                <div>
                    <h2 className="text-center my-8">Browse a few of the characters from {serverDetails.name} Shard: {shardDetails.name}</h2>
                </div>


				{data.charactersOnServer.length ?
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center"> {data.charactersOnServer.map(character => (
							<Link key={`character_${character.id}`} to={`/users/${character.user.username}/characters/${character.id}`} className="underline hover:no-underline">
                                <img
                                    src={getUserImgSrc(character.user.image?.id)}
                                    alt={character.user.username}
                                    className="h-24 w-24 rounded-full object-cover"
                                />
                                {character.name}
                            </Link>
					))}</div> : <div className="text-center">No characters found on this shard yet</div>}

				{/* <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
					{data.charactersOnServer.map(character => (
							<Link key={`char_${character.id}`} to={`/users/${character.user.username}/characters/${character.id}`} className="underline hover:no-underline">{character.name}</Link>
					))}
				</div> */}
			</div>

			<div className='flex flex-row gap-4'>
				{/* <Link to={`/servers/${serverDetails.slug}/shards/${shardDetails.slug}/characters`} className="mt-8 underline hover:no-underline">View Characters</Link> */}
				<Link to={`/servers/${serverDetails.slug}/shards/${shardDetails.slug}/players`} className="mt-8 underline hover:no-underline">View Players</Link>
				<Link to="/servers" className="mt-8 underline hover:no-underline">View list of servers</Link>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.serverDetails.name ?? params.selectedServer
	return [
		{ title: `${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Server ${displayName} on ${brandName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p className="text-center">We do not have a server/shard with the name <br />"{params.serverName}/shards/{params.shardName}"<br /> reach out if you think it should be added.</p>
				),
			}}
		/>
	)
}
